import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, Ticket, Copy, Edit3 } from 'lucide-react';

const TYPES = [
  { value: 'percent_off', label: 'Percentage off', hint: 'e.g. 10% off. Value = percent (10).' },
  { value: 'amount_off', label: 'Flat amount off', hint: 'e.g. $20 off. Value = dollars (20).' },
  { value: 'free_shipping', label: 'Free shipping', hint: 'Shipping cost becomes $0 at checkout.' },
  { value: 'bogo', label: 'Buy X get Y free', hint: 'Cheapest Y items free per group of (X+Y).' },
  { value: 'bundle', label: 'Any N for $X', hint: 'e.g. any 2 socks for $50. Value not used — use bundle_qty + bundle_price.' },
];

const emptyForm = () => ({
  code: '', type: 'percent_off', value: 10, min_spend: 0,
  max_uses: '', expires_at: '', active: true, auto_apply: false, description: '',
  bogo_buy: 2, bogo_get: 1,
  bundle_qty: 2, bundle_price: 50,
  applies_to_slugs: [],
});

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="h-eyebrow mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>{hint}</div>}
    </label>
  );
}

function Input(props) {
  return (
    <input {...props}
      className={`w-full rounded-md border px-3 py-2 text-sm bg-white ${props.className || ''}`}
      style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)', ...(props.style || {}) }} />
  );
}

function CodeEditor({ initial, onClose, onSaved, products }) {
  const [form, setForm] = useState(() => ({
    ...initial,
    max_uses: initial.max_uses ?? '',
    expires_at: initial.expires_at ? initial.expires_at.slice(0, 16) : '',
    applies_to_slugs: initial.applies_to_slugs || [],
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSlug = (slug) => {
    setForm((f) => ({
      ...f,
      applies_to_slugs: f.applies_to_slugs.includes(slug)
        ? f.applies_to_slugs.filter((x) => x !== slug)
        : [...f.applies_to_slugs, slug],
    }));
  };

  const save = async () => {
    if (!form.code || !form.type) { toast.error('Code and type are required.'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value) || 0,
        min_spend: Number(form.min_spend) || 0,
        max_uses: form.max_uses === '' || form.max_uses == null ? null : Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: !!form.active,
        auto_apply: !!form.auto_apply,
        description: form.description || '',
        bogo_buy: Number(form.bogo_buy) || 2,
        bogo_get: Number(form.bogo_get) || 1,
        bundle_qty: Number(form.bundle_qty) || 2,
        bundle_price: Number(form.bundle_price) || 0,
        applies_to_slugs: form.applies_to_slugs || [],
      };
      if (isNew) {
        await api.post('/admin/shop/discount-codes', payload);
        toast.success('Code created');
      } else {
        await api.put(`/admin/shop/discount-codes/${form.id}`, payload);
        toast.success('Saved');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const t = TYPES.find((x) => x.value === form.type);

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="discount-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div>
            <div className="h-eyebrow">{isNew ? 'New discount code' : 'Edit code'}</div>
            <div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{form.code || 'UNTITLED'}</div>
          </div>
          <button onClick={onClose} className="p-1" aria-label="Close" style={{ color: 'var(--heritage-plum)' }}><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Code" hint="Shoppers enter this at checkout. Auto-uppercased.">
              <Input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s+/g, ''))} placeholder="WELCOME10" data-testid="discount-code" />
            </Field>
            <Field label="Type" hint={t?.hint}>
              <select value={form.type} onChange={(e) => set('type', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm bg-white"
                style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                data-testid="discount-type">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>

            {(form.type === 'percent_off' || form.type === 'amount_off') && (
              <Field label={form.type === 'percent_off' ? 'Percent (0–100)' : 'Amount off (NZD)'}>
                <Input type="number" value={form.value} onChange={(e) => set('value', e.target.value)} data-testid="discount-value" />
              </Field>
            )}

            {form.type === 'bogo' && (
              <>
                <Field label="Buy quantity"><Input type="number" value={form.bogo_buy} onChange={(e) => set('bogo_buy', e.target.value)} /></Field>
                <Field label="Get free quantity"><Input type="number" value={form.bogo_get} onChange={(e) => set('bogo_get', e.target.value)} /></Field>
              </>
            )}

            {form.type === 'bundle' && (
              <>
                <Field label="Bundle qty (any N)"><Input type="number" value={form.bundle_qty} onChange={(e) => set('bundle_qty', e.target.value)} /></Field>
                <Field label="Bundle price (NZD)"><Input type="number" value={form.bundle_price} onChange={(e) => set('bundle_price', e.target.value)} /></Field>
              </>
            )}

            <Field label="Minimum spend (NZD)" hint="Set to 0 for no minimum.">
              <Input type="number" value={form.min_spend} onChange={(e) => set('min_spend', e.target.value)} data-testid="discount-min-spend" />
            </Field>
            <Field label="Max uses" hint="Blank = unlimited.">
              <Input type="number" value={form.max_uses} onChange={(e) => set('max_uses', e.target.value)} placeholder="Unlimited" />
            </Field>
            <Field label="Expires at" hint="Blank = never expires.">
              <Input type="datetime-local" value={form.expires_at} onChange={(e) => set('expires_at', e.target.value)} />
            </Field>
            <Field label="Status">
              <select value={form.active ? '1' : '0'} onChange={(e) => set('active', e.target.value === '1')}
                className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>
                <option value="1">Active</option>
                <option value="0">Paused</option>
              </select>
            </Field>
          </div>

          <Field label="Public description (optional)" hint="Shown to the shopper when the code is applied.">
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="10% off welcome offer" />
          </Field>

          <label className="flex items-start gap-2 p-3 rounded-md" style={{ background: 'rgba(75,24,56,0.05)' }}>
            <input type="checkbox" checked={!!form.auto_apply} onChange={(e) => set('auto_apply', e.target.checked)} className="mt-0.5" data-testid="discount-auto-apply" />
            <span className="text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <b>Apply automatically at checkout</b>
              <span className="block text-[11px] opacity-75 mt-0.5">Shoppers don't need to type the code. Great for BOGO offers and launch promos. If multiple auto-apply codes qualify, the best one wins.</span>
            </span>
          </label>

          {(form.type === 'bogo' || form.type === 'bundle' || form.type === 'percent_off') && products.length > 0 && (
            <div>
              <div className="h-eyebrow mb-2">Applies to products (optional)</div>
              <div className="text-[11px] opacity-70 mb-2" style={{ color: 'var(--heritage-plum)' }}>Tick products this code should apply to. Leave all unticked to apply to everything.</div>
              <div className="grid grid-cols-2 gap-1.5">
                {products.map((p) => {
                  const checked = (form.applies_to_slugs || []).includes(p.slug);
                  return (
                    <label key={p.slug} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-[rgba(75,24,56,0.04)]">
                      <input type="checkbox" checked={checked} onChange={() => toggleSlug(p.slug)} />
                      <span style={{ color: 'var(--heritage-plum)' }}>{p.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <button onClick={onClose} className="h-btn-outline text-xs">Cancel</button>
          <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="discount-save">
            <Save size={12} className="mr-1" /> {saving ? 'Saving...' : (isNew ? 'Create code' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShopDiscounts() {
  const [codes, setCodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get('/admin/shop/discount-codes'),
        api.get('/admin/shop/products'),
      ]);
      setCodes(c.data.codes || []);
      setProducts(p.data.products || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete code "${c.code}"?`)) return;
    try {
      await api.delete(`/admin/shop/discount-codes/${c.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c.code);
    toast.success(`Copied ${c.code}`);
  };

  const describe = (c) => {
    if (c.type === 'percent_off') return `${c.value}% off${c.min_spend ? ` over $${c.min_spend}` : ''}`;
    if (c.type === 'amount_off') return `$${c.value} off${c.min_spend ? ` over $${c.min_spend}` : ''}`;
    if (c.type === 'free_shipping') return `Free shipping${c.min_spend ? ` over $${c.min_spend}` : ''}`;
    if (c.type === 'bogo') return `Buy ${c.bogo_buy} get ${c.bogo_get} free`;
    if (c.type === 'bundle') return `Any ${c.bundle_qty} for $${c.bundle_price}`;
    return c.type;
  };

  return (
    <div className="space-y-5" data-testid="admin-shop-discounts">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Discount Codes</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Percentage, flat, free shipping, BOGO, and bundle offers. Applied at checkout when the shopper types the code.</p>
        </div>
        <button onClick={() => setEditing(emptyForm())} className="h-btn text-xs" data-testid="discount-new">
          <Plus size={12} className="mr-1" /> New code
        </button>
      </div>

      {loading && <div className="h-eyebrow">Loading...</div>}
      {!loading && codes.length === 0 && (
        <Panel className="p-8 text-center">
          <Ticket size={32} className="mx-auto opacity-40" style={{ color: 'var(--heritage-plum)' }} />
          <div className="h-serif text-xl mt-3" style={{ color: 'var(--heritage-plum)' }}>No codes yet</div>
          <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Create your first offer.</div>
        </Panel>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {codes.map((c) => (
          <Panel key={c.id} className="p-5" data-testid={`discount-card-${c.code}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="h-display text-2xl" style={{ color: 'var(--heritage-plum)' }}>{c.code}</div>
                <div className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>{describe(c)}</div>
                {c.description && <div className="text-xs italic opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>{c.description}</div>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="h-chip text-[9px]" style={{ background: c.active ? 'rgba(91,110,44,0.18)' : 'rgba(154,44,44,0.15)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{c.active ? 'ACTIVE' : 'PAUSED'}</span>
                {c.auto_apply && (
                  <span className="h-chip text-[9px]" style={{ background: 'rgba(154,75,126,0.25)', borderColor: 'transparent', color: 'var(--heritage-plum)' }} title="Applies automatically \u2014 no code entry needed">AUTO</span>
                )}
              </div>
            </div>
            <div className="text-[11px] opacity-75 mt-3 grid grid-cols-2 gap-1" style={{ color: 'var(--heritage-plum)' }}>
              <div>Uses: {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</div>
              <div>{c.expires_at ? `Expires ${new Date(c.expires_at).toLocaleDateString('en-NZ')}` : 'No expiry'}</div>
              <div>{c.min_spend ? `Min spend $${c.min_spend}` : 'No minimum'}</div>
              <div>{(c.applies_to_slugs || []).length ? `${c.applies_to_slugs.length} product(s)` : 'All products'}</div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <button onClick={() => copyCode(c)} className="h-btn-outline text-xs"><Copy size={12} className="mr-1" /> Copy</button>
              <button onClick={() => setEditing(c)} className="h-btn-outline text-xs" data-testid={`discount-edit-${c.code}`}><Edit3 size={12} className="mr-1" /> Edit</button>
              <button onClick={() => remove(c)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100" style={{ color: '#9A2C2C' }} data-testid={`discount-delete-${c.code}`}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </Panel>
        ))}
      </div>

      {editing && <CodeEditor initial={editing} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
